import React from "react";
import { motion } from "framer-motion";
import { MapPin, Calendar, Globe2 } from "lucide-react";

const FACTS = [
  { icon: Calendar, label: "Founded", value: "2025" },
  { icon: MapPin, label: "Headquarters", value: "United States" },
  { icon: Globe2, label: "Structure", value: "Remote-first" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="section-about"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative aspect-[4/5] overflow-hidden"
          >
            <img
              src="https://images.pexels.com/photos/29644165/pexels-photo-29644165.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
              alt="F3 Media headquarters reflection"
              className="absolute inset-0 w-full h-full object-cover grayscale contrast-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
                F3 · MMXXV
              </div>
              <div className="font-mono text-[10px] tracking-[0.3em] text-violet-300 uppercase">
                Signal Over Noise
              </div>
            </div>
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
              <span>About</span>
              <span>06 / 08</span>
            </div>
          </motion.div>

          {/* Content */}
          <div className="lg:col-span-7">
            <div className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase mb-6">
              Chapter 05 · About
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0] mb-8">
              Building infrastructure
              <br />
              for the{" "}
              <span className="text-zinc-400">future of media</span>
            </h2>

            <div className="space-y-5 text-zinc-300 text-base md:text-[17px] leading-relaxed max-w-2xl">
              <p>
                Founded in <span className="text-white">2025</span>, F3 Media is a{" "}
                <span className="text-white">remote-first</span> organization
                headquartered in the United States, building a hybrid{" "}
                <span className="text-white">Media PaaS</span> platform for
                high-integrity content creation and consumption.
              </p>
              <p className="text-zinc-400">
                We exist to invert the incentives of the modern internet — to
                build a stack where signal is rewarded, depth is recognized, and
                the creators, viewers and brands at every node compound value
                together.
              </p>
            </div>

            {/* Facts strip */}
            <div className="mt-12 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              {FACTS.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.label} className="bg-black p-5">
                    <Icon className="w-4 h-4 text-violet-300 mb-3" strokeWidth={1.5} />
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-1">
                      {f.label}
                    </div>
                    <div className="text-white text-sm font-medium">{f.value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
